import { model as config, remote } from '@/config'
import type {
  IContentInfoOVP,
  IEpgInfoOVP,
  VideoSourceInfoOVP,
} from '@setplex/tria-api'
import { api } from '@setplex/tria-api'
import {
  attach,
  createEvent,
  createStore,
  sample,
  Store,
  type EventCallable,
} from 'effector'
import { v4 as uuidv4 } from 'uuid'
import { model as session } from '~/entities/session'
import { selectEpgChannelModel } from '~/features/epg/channels/select'
import { selectEpgDateModel } from '~/features/epg/dates'
import { $session } from '~/features/session/model'
import { ONE_DAY_MS, PlaybackTriggerEnum } from '~/shared/constants'
import { aye, readFbc, readFbp } from '~/shared/helpers'
import { $watchId } from '~/widgets/player/analytics'
import { channelsEntity } from '../channels'
import { epgDatesModel } from '../epg/dates'

export const play: EventCallable<VideoSourceInfoOVP> = createEvent()
export const stop: EventCallable<VideoSourceInfoOVP> = createEvent()

/** Fired when play_stop is handed to Analytix (replaces legacy OVP `sendAnalyticsStopPauseWatching`). */
export const playStopAnalytixDispatched = createEvent<void>()
export const playerOpen: EventCallable<IContentInfoOVP> = createEvent()
export const playerWatching: EventCallable<VideoSourceInfoOVP> = createEvent()
export const playerEvent: EventCallable<VideoSourceInfoOVP> = createEvent()
export const playError: EventCallable<VideoSourceInfoOVP> = createEvent()

// Used to generate playerSessionId
export const setIsPlayerJustOpened = createEvent<boolean>()
export const $isPlayerJustOpened = createStore(false).on(
  setIsPlayerJustOpened,
  (_, data) => data
)

export const setPlayerSessionId = createEvent<string>()
export const $playerSessionId = createStore<string>('').on(
  setPlayerSessionId,
  (_, data) => data
)

// generate playerSessionId

sample({
  clock: setIsPlayerJustOpened,
  filter: aye,
  fn: () => uuidv4(),
  target: setPlayerSessionId,
})

// Collect data about playbackTrigger
export const setPlaybackTrigerData = createEvent<PlaybackTriggerEnum>()

export const $playbackTrigerData = createStore<PlaybackTriggerEnum>(
  PlaybackTriggerEnum.playerOpen
).on(setPlaybackTrigerData, (_, data) => data)

// store to collect data received from player (duration, audioLanguage, textLanguage, startedTime etc.)
// need to store as for live we should wait for data about epg
// need only for event 'play_start', there is no necessity for other events for current selected channel (as will be used received data)
export const resetPlayerData = createEvent()

export const $playerData = createStore<VideoSourceInfoOVP | null>(null) //
  .reset(resetPlayerData)
  .on(play, (_, data) => data)

// store and events for 'epg_view' event analytics
export const sendEpgViewEventData = createEvent<string | undefined>()
export const $epgViewEventData = createStore<IEpgInfoOVP | null>(null)

export const $activeChannel = createStore<
  typeof channelsEntity.$channel.defaultState
>(null)
  .on(channelsEntity.$channel, (_, channel) => channel)
  .on(selectEpgChannelModel.$active, (_, channel) => channel)

sample({
  source: {
    current: $activeChannel,
    activeDay: selectEpgDateModel.$active,
    today: epgDatesModel.$today,
  },
  filter: ({ current, activeDay, today }) =>
    Boolean(current) && Boolean(activeDay) && Boolean(today),
  fn: ({ current, activeDay, today }) => {
    if (!current || !activeDay || !today) return null
    const todayInMS = new Date(today.year, today.month, today.day).getTime()
    const activeDayInMs = new Date(
      activeDay.year,
      activeDay.month,
      activeDay.day
    ).getTime()
    const tabId = (activeDayInMs - todayInMS) / ONE_DAY_MS

    return {
      channelId: current.id ? String(current.id) : '',
      channelTitle: current.title || '',
      tabId: String(tabId),
    }
  },
  target: $epgViewEventData,
})

// Firebase flag value
export const $playerWatchingEventInterval: Store<number | undefined> =
  config.get(remote.uvo_playerWatchingEventInterval)

const seoTrackEventFx = attach({ effect: api.seo.base.trackEventFx })

export type SeoCapiTrackEventBase = {
  event_name: string
  event_time: number
  action_source: string
  event_source_url: string
  event_id: string
}

export const seoEventTracking = createEvent<SeoCapiTrackEventBase>()

export function buildSeoCapiTrackPayload(
  receivedData: SeoCapiTrackEventBase,
  {
    info,
    session: sessionRow,
    watchId,
  }: {
    info: ReturnType<typeof session.$info.getState>
    session: ReturnType<typeof $session.getState>
    watchId: ReturnType<typeof $watchId.getState>
  }
) {
  return {
    ...receivedData,
    session_id: sessionRow?.id,
    user_guid: info?.userGuid,
    fbp: readFbp(),
    fbc: readFbc(),
    watch_id: String(watchId),
  }
}

sample({
  clock: seoEventTracking,
  source: { info: session.$info, session: $session, watchId: $watchId },
  fn: ({ info, session, watchId }, receivedData) =>
    buildSeoCapiTrackPayload(receivedData, { info, session, watchId }),
  target: seoTrackEventFx,
})

/** Resolves when the CAPI HTTP request for this event_id has settled (success or failure). */
export function awaitSeoCapiEventTrack(
  receivedData: SeoCapiTrackEventBase
): Promise<void> {
  const payload = buildSeoCapiTrackPayload(receivedData, {
    // Snapshot at click time (same instant the reactive sample would read sources).
    /* eslint-disable effector/no-getState */
    info: session.$info.getState(),
    session: $session.getState(),
    watchId: $watchId.getState(),
    /* eslint-enable effector/no-getState */
  })
  const correlationId = payload.event_id

  return new Promise((resolve) => {
    const unsub = seoTrackEventFx.finally.watch(({ params }) => {
      const p = params as SeoCapiTrackEventBase
      if (p?.event_id !== correlationId) return
      unsub()
      resolve()
    })
    seoTrackEventFx(payload)
  })
}
